import React, { useEffect, useState } from "react";
import { FileText, Search, Phone, Calendar, IndianRupee, AlertTriangle } from "lucide-react";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function AgentPolicies() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/agent/policies").then((r) => setList(r.data));
  }, []);

  const filtered = list.filter((p) => {
    const s = q.toLowerCase();
    return !q ||
      (p.customer_name || "").toLowerCase().includes(s) ||
      (p.customer_phone || "").includes(s) ||
      (p.policy_number || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="agent-policies-page">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">Customer Policies</h1>
        <p className="text-sm text-slate-500">{list.length} total · Read-only view</p>
      </div>

      <div className="mt-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="agent-policies-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, phone or policy #"
          className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-tenant-primary bg-white"
        />
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((p) => {
          const remaining = Math.max(0, (p.premium_amount || 0) - (p.collected_amount || 0));
          const progress = p.premium_amount ? Math.min(100, Math.round(((p.collected_amount || 0) / p.premium_amount) * 100)) : 0;
          return (
            <div key={p.id} data-testid={`agent-policy-${p.id}`} className="bg-white border border-slate-100 rounded-2xl p-4 card-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-extrabold tracking-tight truncate">{p.customer_name}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> +91 {p.customer_phone}</div>
                </div>
                <span className={`text-[9px] uppercase font-bold px-2 py-1 rounded-full ${p.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.status}</span>
              </div>

              <div className="mt-3 flex items-center gap-2 text-tenant-primary">
                <FileText className="w-4 h-4" />
                <span className="font-bold text-sm">{p.policy_name}</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-0.5">#{p.policy_number}</div>

              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <Item label="Premium" value={fmtINR(p.premium_amount)} />
                <Item label="Daily" value={fmtINR(p.daily_amount)} />
                <Item label="Frequency" value={(p.frequency || "").replaceAll("_", " ")} />
                <Item label="Start" value={fmtDate(p.start_date)} icon={Calendar} />
                {p.end_date && <Item label="End" value={fmtDate(p.end_date)} icon={Calendar} />}
                <Item label="Next Due" value={fmtDate(p.due_date)} icon={Calendar} />
              </div>

              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-bold text-slate-700">Collected</span>
                  <span className="font-extrabold text-tenant-primary">{progress}%</span>
                </div>
                <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full emerald-grad" style={{ width: `${progress}%` }} />
                </div>
                <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
                  <span>{fmtINR(p.collected_amount || 0)}</span>
                  <span>Remaining {fmtINR(remaining)}</span>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center text-slate-400 text-sm py-12 col-span-full">
            <FileText className="w-10 h-10 mx-auto opacity-50" />
            <div className="mt-2">No policies found.</div>
          </div>
        )}
      </div>
    </div>
  );
}

const Item = ({ label, value, icon: Icon }) => (
  <div className="bg-slate-50 rounded-lg p-2">
    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400">{label}</div>
    <div className="font-bold text-slate-800 capitalize text-[11px] inline-flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3 text-slate-400" />} {value}
    </div>
  </div>
);
